import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

/* ─── Types ─── */

interface Report {
  id: string;
  report_number: string;
  title: string;
  revision: string;
  revision_number: number;
  issue_date: string;
  author: string;
  reviewer: string | null;
  scope_description: string | null;
  status: string;
  area_id: string | null;
}

interface ModifiedSetting {
  setting_name: string;
  previous_value: string | null;
  new_value: string;
  unit: string | null;
  category: string;
  notes: string | null;
}

interface SettingChangeDetail {
  id: string;
  device_tag: string;
  device_type: string | null;
  manufacturer: string | null;
  model: string | null;
  status: string;
  priority: string;
  recommended_date: string | null;
  acknowledged_date: string | null;
  implemented_date: string | null;
  verified_date: string | null;
  notes: string | null;
  modified_settings: ModifiedSetting[];
}

interface ReportDetail {
  id: string;
  setting_changes: SettingChangeDetail[];
  total_changes: number;
  changes_verified: number;
  changes_implemented: number;
  changes_pending: number;
}

interface Area {
  id: string;
  name: string;
  area_code: string;
  display_order: number;
}

interface ReportGroup {
  report_number: string;
  title: string;
  current: Report | null;
  archive: Report[];
  area_id: string | null;
}

/* ─── Helpers ─── */

const changeStatusConfig = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'verified': return { color: '#4caf50', bg: '#4caf5015', border: '#4caf5033', label: '✅ Verified' };
    case 'implemented': return { color: '#2196f3', bg: '#2196f315', border: '#2196f333', label: '🔧 Implemented' };
    case 'scheduled': return { color: '#ff9800', bg: '#ff980015', border: '#ff980033', label: '📅 Scheduled' };
    case 'acknowledged': return { color: '#ff9800', bg: '#ff980015', border: '#ff980033', label: '👁️ Acknowledged' };
    case 'recommended': return { color: '#f44336', bg: '#f4433615', border: '#f4433633', label: '⏳ Pending' };
    default: return { color: '#888', bg: '#88888815', border: '#88888833', label: status };
  }
};

const priorityConfig = (p: string) => {
  switch (p?.toLowerCase()) {
    case 'high': return { color: '#f44336', label: 'HIGH' };
    case 'medium': return { color: '#ff9800', label: 'MED' };
    case 'low': return { color: '#4caf50', label: 'LOW' };
    default: return { color: '#888', label: p };
  }
};

const formatDate = (d: string | null): string => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const nextStatusAction = (current: string): { status: string; label: string; color: string } | null => {
  switch (current?.toLowerCase()) {
    case 'recommended': return { status: 'acknowledged', label: 'Acknowledge', color: '#ff9800' };
    case 'acknowledged': return { status: 'implemented', label: 'Mark Implemented', color: '#2196f3' };
    case 'scheduled': return { status: 'implemented', label: 'Mark Implemented', color: '#2196f3' };
    case 'implemented': return { status: 'verified', label: 'Mark Verified', color: '#4caf50' };
    default: return null;
  }
};

const groupReports = (reports: Report[]): ReportGroup[] => {
  const map: Record<string, Report[]> = {};
  reports.forEach((r) => {
    const key = r.report_number || r.id;
    if (!map[key]) map[key] = [];
    map[key].push(r);
  });
  return Object.entries(map).map(([num, items]) => {
    items.sort((a, b) => b.revision_number - a.revision_number);
    const current = items.find((i) => i.status === 'issued') || items[0];
    const archive = items.filter((i) => i.id !== current.id);
    return { report_number: num, title: current.title, current, archive, area_id: current.area_id };
  });
};

/* ─── Main Component ─── */

const ReportsLibrary: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [siteName, setSiteName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedReports, setExpandedReports] = useState<Record<string, boolean>>({});
  const [expandedArchives, setExpandedArchives] = useState<Record<string, boolean>>({});
  const [reportDetails, setReportDetails] = useState<Record<string, ReportDetail>>({});
  const [loadingDetails, setLoadingDetails] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!siteId) return;
    const load = async () => {
      try {
        const [siteRes, reportsRes, areasRes] = await Promise.all([
          api.get(`/api/sites/${siteId}`),
          api.get(`/api/sites/${siteId}/reports`),
          api.get(`/api/sites/${siteId}/areas`),
        ]);
        setSiteName(siteRes.data.name);
        setReports(reportsRes.data || []);
        setAreas(areasRes.data || []);
      } catch {
        setError('Failed to load reports.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [siteId]);

  const loadDetail = async (reportId: string) => {
    setLoadingDetails((prev) => ({ ...prev, [reportId]: true }));
    try {
      const res = await api.get(`/api/reports/${reportId}/detail`);
      setReportDetails((prev) => ({ ...prev, [reportId]: res.data }));
    } catch {
      // silently fail
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [reportId]: false }));
    }
  };

  const toggleExpand = async (reportId: string) => {
    const isOpen = expandedReports[reportId];
    setExpandedReports((prev) => ({ ...prev, [reportId]: !isOpen }));
    if (!isOpen && !reportDetails[reportId] && !loadingDetails[reportId]) {
      loadDetail(reportId);
    }
  };

  const toggleArchive = (key: string) => {
    setExpandedArchives((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleStatusUpdate = async (changeId: string, newStatus: string, reportId: string, notes?: string) => {
    try {
      const payload: any = { status: newStatus };
      if (notes) payload.notes = notes;
      await api.patch(`/api/setting-changes/${changeId}/status`, payload);
      loadDetail(reportId);
    } catch (err: any) {
      throw err; // Let the card handle the error display
    }
  };

  const handleDownload = (reportId: string, filename: string) => {
    // Reports don't have file storage yet — placeholder
    alert(`Download not yet available. Report PDF upload coming soon.\n\nFile: ${filename}`);
  };

  // Group by area
  const areaMap = Object.fromEntries(areas.map((a) => [a.id, a]));
  const sortedAreas = [...areas].sort((a, b) => a.display_order - b.display_order);
  const reportsByArea: Record<string, Report[]> = {};
  const noArea: Report[] = [];
  reports.forEach((r) => {
    if (r.area_id && areaMap[r.area_id]) {
      if (!reportsByArea[r.area_id]) reportsByArea[r.area_id] = [];
      reportsByArea[r.area_id].push(r);
    } else {
      noArea.push(r);
    }
  });

  return (
    <div style={{
      minHeight: '100vh', background: '#f5f6fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex', flexDirection: 'column',
    }}>
      <header style={{
        background: '#1a1a2e', color: '#fff', padding: '0 32px', height: 64,
        display: 'flex', alignItems: 'center', gap: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)', flexShrink: 0,
      }}>
        <button onClick={() => navigate(`/site/${siteId}`)} style={{
          background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
          color: '#fff', padding: '7px 16px', borderRadius: 6, cursor: 'pointer',
          fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
        }}>← Site Overview</button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{siteName || 'Protection Reports'}</div>
          <div style={{ fontSize: 11, color: '#8899aa', letterSpacing: 1 }}>📋 PROTECTION REPORTS LIBRARY</div>
        </div>
      </header>

      <main style={{ flex: 1, padding: 32, maxWidth: 1200, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        {loading && <div style={{ textAlign: 'center', color: '#888', padding: 60 }}>Loading reports...</div>}
        {error && (
          <div style={{ background: '#fff5f5', border: '1px solid #f44336', borderRadius: 8, color: '#f44336', padding: '14px 18px', marginBottom: 24 }}>{error}</div>
        )}
        {!loading && reports.length === 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: '60px 40px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
            <div style={{ fontSize: 16, color: '#666' }}>No reports found for this site.</div>
          </div>
        )}
        {!loading && reports.length > 0 && (
          <>
            {sortedAreas.map((area) => {
              const areaReports = reportsByArea[area.id];
              if (!areaReports || areaReports.length === 0) return null;
              return (
                <AreaSection key={area.id} area={area} groups={groupReports(areaReports)}
                  expandedReports={expandedReports} expandedArchives={expandedArchives}
                  reportDetails={reportDetails} loadingDetails={loadingDetails}
                  onToggleExpand={toggleExpand} onToggleArchive={toggleArchive}
                  onStatusUpdate={handleStatusUpdate} onDownload={handleDownload} />
              );
            })}
            {noArea.length > 0 && (
              <AreaSection area={{ id: 'none', name: 'General / Site-Wide', area_code: '', display_order: 99 }}
                groups={groupReports(noArea)} expandedReports={expandedReports} expandedArchives={expandedArchives}
                reportDetails={reportDetails} loadingDetails={loadingDetails}
                onToggleExpand={toggleExpand} onToggleArchive={toggleArchive}
                onStatusUpdate={handleStatusUpdate} onDownload={handleDownload} />
            )}
          </>
        )}
      </main>

      <footer style={{ background: '#1a1a2e', color: '#8899aa', textAlign: 'center', padding: '14px', fontSize: 12, flexShrink: 0 }}>
        © 2026 JMB Engineering Pty Ltd — Protection Platform
      </footer>
    </div>
  );
};

/* ─── Area Section ─── */

interface SectionProps {
  area: Area;
  groups: ReportGroup[];
  expandedReports: Record<string, boolean>;
  expandedArchives: Record<string, boolean>;
  reportDetails: Record<string, ReportDetail>;
  loadingDetails: Record<string, boolean>;
  onToggleExpand: (reportId: string) => void;
  onToggleArchive: (key: string) => void;
  onStatusUpdate: (changeId: string, newStatus: string, reportId: string, notes?: string) => Promise<void>;
  onDownload: (reportId: string, filename: string) => void;
}

const AreaSection: React.FC<SectionProps> = ({
  area, groups, expandedReports, expandedArchives, reportDetails, loadingDetails,
  onToggleExpand, onToggleArchive, onStatusUpdate, onDownload,
}) => (
  <section style={{ marginBottom: 40 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, paddingBottom: 12, borderBottom: '3px solid #0f3460' }}>
      <span style={{ fontSize: 28 }}>📋</span>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{area.name}</h2>
      {area.area_code && (
        <span style={{ background: '#1a1a2e', color: '#fff', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{area.area_code}</span>
      )}
      <span style={{ marginLeft: 'auto', background: '#0f346015', color: '#0f3460', border: '1px solid #0f346033', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600 }}>
        {groups.length} report{groups.length !== 1 ? ' series' : ''}
      </span>
    </div>
    {groups.map((group, gi) => (
      <ReportGroupCard key={gi} group={group} index={gi}
        expandedReports={expandedReports} expandedArchives={expandedArchives}
        reportDetails={reportDetails} loadingDetails={loadingDetails}
        onToggleExpand={onToggleExpand} onToggleArchive={onToggleArchive}
        onStatusUpdate={onStatusUpdate} onDownload={onDownload} />
    ))}
  </section>
);

/* ─── Report Group Card ─── */

interface CardProps {
  group: ReportGroup;
  index: number;
  expandedReports: Record<string, boolean>;
  expandedArchives: Record<string, boolean>;
  reportDetails: Record<string, ReportDetail>;
  loadingDetails: Record<string, boolean>;
  onToggleExpand: (reportId: string) => void;
  onToggleArchive: (key: string) => void;
  onStatusUpdate: (changeId: string, newStatus: string, reportId: string, notes?: string) => Promise<void>;
  onDownload: (reportId: string, filename: string) => void;
}

const ReportGroupCard: React.FC<CardProps> = ({
  group, index, expandedReports, expandedArchives, reportDetails, loadingDetails,
  onToggleExpand, onToggleArchive, onStatusUpdate, onDownload,
}) => {
  const current = group.current;
  if (!current) return null;

  const isExpanded = expandedReports[current.id] || false;
  const detail = reportDetails[current.id];
  const isLoading = loadingDetails[current.id] || false;
  const archiveKey = `archive-${group.report_number}-${index}`;
  const isArchiveOpen = expandedArchives[archiveKey] || false;

  return (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: 20, overflow: 'hidden' }}>
      {/* Header bar */}
      <div style={{ background: '#0f3460', color: '#fff', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 6, padding: '3px 10px', fontSize: 13, fontWeight: 700, fontFamily: 'monospace' }}>{group.report_number}</span>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{group.title}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '3px 12px', fontSize: 11, fontWeight: 600 }}>Current: {current.revision}</span>
          {group.archive.length > 0 && (
            <span style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '3px 10px', fontSize: 11 }}>
              {group.archive.length} prior revision{group.archive.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Current report summary */}
      <div style={{ padding: '18px 20px', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ width: 4, minHeight: 50, borderRadius: 2, background: '#4caf50', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ background: '#0f346015', color: '#0f3460', border: '1px solid #0f346033', borderRadius: 6, padding: '3px 10px', fontSize: 13, fontWeight: 700, fontFamily: 'monospace' }}>{current.revision}</span>
              <span style={{ background: '#4caf5015', color: '#4caf50', border: '1px solid #4caf5033', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>● Current</span>
              <span style={{ fontSize: 13, color: '#555' }}>{formatDate(current.issue_date)}</span>
              <span style={{ fontSize: 12, color: '#888' }}>by {current.author}</span>
              {current.reviewer && <span style={{ fontSize: 12, color: '#888' }}>· Reviewed: {current.reviewer}</span>}
            </div>
            {current.scope_description && (
              <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5, marginBottom: 6 }}>{current.scope_description}</div>
            )}
            {detail && detail.total_changes > 0 && (
              <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                <MiniStat label="Changes" value={detail.total_changes} color="#0f3460" />
                <MiniStat label="Implemented" value={detail.changes_implemented} color="#2196f3" />
                <MiniStat label="Verified" value={detail.changes_verified} color="#4caf50" />
                <MiniStat label="Pending" value={detail.changes_pending} color={detail.changes_pending > 0 ? '#f44336' : '#4caf50'} />
              </div>
            )}
          </div>
          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => onDownload(current.id, `${group.report_number}_${current.revision}.pdf`)}
              style={{ background: '#0f3460', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
              ⬇ Download
            </button>
            <button onClick={() => onToggleExpand(current.id)}
              style={{ background: isExpanded ? '#0f3460' : '#f5f6fa', color: isExpanded ? '#fff' : '#0f3460', border: `1px solid ${isExpanded ? '#0f3460' : '#ddd'}`, borderRadius: 8, padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              {isExpanded ? '▲ Collapse' : '▼ Setting Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <div style={{ padding: '20px', background: '#fafbfc', borderBottom: '1px solid #f0f0f0' }}>
          {isLoading && <div style={{ textAlign: 'center', color: '#888', padding: 30 }}>Loading setting change details...</div>}
          {!isLoading && detail && detail.total_changes === 0 && (
            <div style={{ textAlign: 'center', color: '#888', padding: '24px', background: '#fff', borderRadius: 8, border: '1px solid #e8eaf0' }}>
              <div style={{ fontSize: 20, marginBottom: 8 }}>✅</div>
              No protection setting changes recommended in this revision.
            </div>
          )}
          {!isLoading && detail && detail.total_changes > 0 && (
            <>
              {/* Progress bar */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap', padding: '14px 18px', background: '#fff', borderRadius: 8, border: '1px solid #e8eaf0', alignItems: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginRight: 8 }}>Implementation Status</div>
                <StatusPill count={detail.changes_verified} label="Verified" color="#4caf50" />
                <StatusPill count={detail.changes_implemented - detail.changes_verified} label="Implemented" color="#2196f3" />
                <StatusPill count={detail.changes_pending} label="Pending" color="#f44336" />
                <div style={{ flex: 1, minWidth: 120, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#e8eaf0', overflow: 'hidden', display: 'flex' }}>
                    <div style={{ width: `${(detail.changes_verified / detail.total_changes) * 100}%`, background: '#4caf50' }} />
                    <div style={{ width: `${((detail.changes_implemented - detail.changes_verified) / detail.total_changes) * 100}%`, background: '#2196f3' }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>
                    {Math.round((detail.changes_implemented / detail.total_changes) * 100)}%
                  </span>
                </div>
              </div>
              {detail.setting_changes.map((change) => (
                <SettingChangeCard key={change.id} change={change} reportId={current.id} onStatusUpdate={onStatusUpdate} />
              ))}
            </>
          )}
        </div>
      )}

      {/* Archive */}
      {group.archive.length > 0 && (
        <>
          <button onClick={() => onToggleArchive(archiveKey)}
            style={{ width: '100%', background: '#f5f6fa', border: 'none', borderTop: '1px solid #e8eaf0', borderBottom: isArchiveOpen ? '1px solid #e8eaf0' : 'none', padding: '10px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#666', fontFamily: 'inherit' }}>
            <span style={{ transform: isArchiveOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▶</span>
            Revision History ({group.archive.length} prior revision{group.archive.length !== 1 ? 's' : ''})
          </button>
          {isArchiveOpen && group.archive.map((r) => {
            const archiveExpanded = expandedReports[r.id] || false;
            const archiveDetail = reportDetails[r.id];
            const archiveLoading = loadingDetails[r.id] || false;
            return (
              <div key={r.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ padding: '14px 20px', display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                  <div style={{ width: 4, minHeight: 40, borderRadius: 2, background: '#ddd', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ background: '#f0f0f0', color: '#888', border: '1px solid #ddd', borderRadius: 6, padding: '3px 10px', fontSize: 13, fontWeight: 700, fontFamily: 'monospace' }}>{r.revision}</span>
                      <span style={{ background: '#ff980015', color: '#ff9800', border: '1px solid #ff980033', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>Superseded</span>
                      <span style={{ fontSize: 13, color: '#888' }}>{formatDate(r.issue_date)}</span>
                      <span style={{ fontSize: 12, color: '#aaa' }}>by {r.author}</span>
                    </div>
                    {r.scope_description && (
                      <div style={{ fontSize: 13, color: '#888', lineHeight: 1.5, marginTop: 6 }}>{r.scope_description}</div>
                    )}
                    {archiveExpanded && (
                      <div style={{ marginTop: 12 }}>
                        {archiveLoading && <div style={{ color: '#888', fontSize: 13, padding: 10 }}>Loading...</div>}
                        {!archiveLoading && archiveDetail && archiveDetail.total_changes === 0 && (
                          <div style={{ color: '#888', fontSize: 13, padding: 10, background: '#f5f6fa', borderRadius: 6 }}>No setting changes in this revision.</div>
                        )}
                        {!archiveLoading && archiveDetail && archiveDetail.total_changes > 0 && (
                          <div style={{ background: '#f5f6fa', borderRadius: 8, padding: 14 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 10 }}>
                              {archiveDetail.total_changes} change{archiveDetail.total_changes !== 1 ? 's' : ''} — {archiveDetail.changes_verified} verified, {archiveDetail.changes_pending} pending
                            </div>
                            {archiveDetail.setting_changes.map((change) => (
                              <SettingChangeCard key={change.id} change={change} reportId={r.id} onStatusUpdate={onStatusUpdate} compact />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => onDownload(r.id, `${group.report_number}_${r.revision}.pdf`)}
                      style={{ background: '#e8eaf0', color: '#555', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
                      ⬇
                    </button>
                    <button onClick={() => onToggleExpand(r.id)}
                      style={{ background: archiveExpanded ? '#666' : '#e8eaf0', color: archiveExpanded ? '#fff' : '#666', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                      {archiveExpanded ? '▲' : '▼ Detail'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

/* ─── Setting Change Card ─── */

const statusOrderList = ['recommended', 'acknowledged', 'scheduled', 'implemented', 'verified'];

const SettingChangeCard: React.FC<{
  change: SettingChangeDetail;
  reportId: string;
  onStatusUpdate: (changeId: string, newStatus: string, reportId: string, notes?: string) => Promise<void>;
  compact?: boolean;
}> = ({ change, reportId, onStatusUpdate, compact }) => {
  const sc = changeStatusConfig(change.status);
  const pc = priorityConfig(change.priority);
  const action = nextStatusAction(change.status);

  const [advancing, setAdvancing] = React.useState(false);
  const [showRevert, setShowRevert] = React.useState(false);
  const [revertTarget, setRevertTarget] = React.useState('');
  const [revertReason, setRevertReason] = React.useState('');
  const [reverting, setReverting] = React.useState(false);
  const [actionError, setActionError] = React.useState('');

  const currentIdx = statusOrderList.indexOf(change.status?.toLowerCase());
  const previousStatuses = statusOrderList.filter((_, i) => i < currentIdx);

  const handleAdvance = async () => {
    if (!action) return;
    setAdvancing(true);
    setActionError('');
    try {
      await onStatusUpdate(change.id, action.status, reportId);
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || 'Failed to advance status.');
    } finally {
      setAdvancing(false);
    }
  };

  const handleRevert = async () => {
    if (!revertTarget || !revertReason.trim()) return;
    setReverting(true);
    setActionError('');
    try {
      await onStatusUpdate(change.id, revertTarget, reportId, revertReason.trim());
      setShowRevert(false);
      setRevertReason('');
      setRevertTarget('');
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || 'Failed to revert status.');
    } finally {
      setReverting(false);
    }
  };

  return (
    <div style={{
      background: '#fff', border: `1px solid ${sc.border}`, borderLeft: `4px solid ${sc.color}`,
      borderRadius: 8, padding: compact ? '12px 14px' : '16px 18px', marginBottom: compact ? 8 : 14,
    }}>
      {/* Device header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: compact ? 8 : 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: compact ? 13 : 14, color: '#1a1a2e' }}>{change.device_tag}</span>
        {change.device_type && (
          <span style={{ background: '#f5f6fa', border: '1px solid #ddd', borderRadius: 4, padding: '2px 8px', fontSize: 11, color: '#666', fontWeight: 500 }}>
            {change.device_type.toUpperCase()}
          </span>
        )}
        {change.manufacturer && <span style={{ fontSize: 11, color: '#999' }}>{change.manufacturer} {change.model}</span>}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ background: `${pc.color}15`, color: pc.color, border: `1px solid ${pc.color}33`, borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>{pc.label}</span>
          <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: 20, padding: '2px 12px', fontSize: 11, fontWeight: 600 }}>{sc.label}</span>
        </div>
      </div>

      {/* Settings table */}
      {change.modified_settings.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 }}>
          <thead>
            <tr style={{ background: '#f5f6fa' }}>
              {['Setting', 'Previous', '', 'New Value', 'Unit'].map((h) => (
                <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#666', borderBottom: '1px solid #e8eaf0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {change.modified_settings.map((s, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '7px 10px', fontWeight: 600, color: '#333' }}>{s.setting_name}</td>
                <td style={{ padding: '7px 10px', color: '#999', textDecoration: 'line-through' }}>{s.previous_value || '—'}</td>
                <td style={{ padding: '7px 10px', color: '#0f3460', fontWeight: 700 }}>→</td>
                <td style={{ padding: '7px 10px', color: '#0f3460', fontWeight: 700 }}>{s.new_value}</td>
                <td style={{ padding: '7px 10px', color: '#888' }}>{s.unit || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Timeline */}
      {!compact && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: '#888' }}>
          <TimelineStep label="Recommended" date={change.recommended_date} done={true} />
          <TimelineStep label="Acknowledged" date={change.acknowledged_date} done={['acknowledged', 'scheduled', 'implemented', 'verified'].includes(change.status)} />
          <TimelineStep label="Implemented" date={change.implemented_date} done={['implemented', 'verified'].includes(change.status)} />
          <TimelineStep label="Verified" date={change.verified_date} done={change.status === 'verified'} />
        </div>
      )}

      {change.notes && !compact && (
        <div style={{ fontSize: 12, color: '#666', marginTop: 8, fontStyle: 'italic', lineHeight: 1.4 }}>{change.notes}</div>
      )}

      {/* Action buttons — advance + revert */}
      {!compact && (
        <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
          {actionError && (
            <div style={{ background: '#fff5f5', border: '1px solid #f44336', borderRadius: 6, padding: '6px 12px', fontSize: 12, color: '#f44336', marginBottom: 8 }}>
              {actionError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {action && (
              <button
                onClick={handleAdvance}
                disabled={advancing}
                style={{
                  background: advancing ? '#ccc' : '#4caf50', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '6px 14px', cursor: advancing ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                }}
              >
                {advancing ? '...' : `→ ${action.label}`}
              </button>
            )}
            {previousStatuses.length > 0 && (
              <button
                onClick={() => { setShowRevert(!showRevert); setActionError(''); }}
                style={{
                  background: showRevert ? '#fff3e0' : '#fff', color: '#e65100',
                  border: '1px solid #ffcc80', borderRadius: 6, padding: '6px 14px',
                  cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                }}
              >
                ↩ Revert
              </button>
            )}
          </div>

          {/* Revert inline form */}
          {showRevert && previousStatuses.length > 0 && (
            <div style={{
              marginTop: 10, background: '#fff8f0', border: '1px solid #ffcc80',
              borderRadius: 8, padding: 14,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>Revert to:</div>
              <select
                value={revertTarget}
                onChange={(e) => setRevertTarget(e.target.value)}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 6,
                  border: '1px solid #ddd', fontSize: 13, marginBottom: 8, fontFamily: 'inherit',
                }}
              >
                <option value="">Select stage...</option>
                {previousStatuses.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Reason for revert (required)"
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 6,
                  border: '1px solid #ddd', fontSize: 13, marginBottom: 8,
                  fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
              <button
                onClick={handleRevert}
                disabled={reverting || !revertTarget || !revertReason.trim()}
                style={{
                  background: reverting || !revertTarget || !revertReason.trim() ? '#ccc' : '#e65100',
                  color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px',
                  cursor: reverting || !revertTarget || !revertReason.trim() ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                }}
              >
                {reverting ? 'Reverting...' : 'Confirm Revert'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Small Components ─── */

const MiniStat: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    <div style={{ fontSize: 10, color: '#999' }}>{label}</div>
  </div>
);

const StatusPill: React.FC<{ count: number; label: string; color: string }> = ({ count, label, color }) => (
  <span style={{ background: `${color}15`, color, border: `1px solid ${color}33`, borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
    {count} {label}
  </span>
);

const TimelineStep: React.FC<{ label: string; date: string | null; done: boolean }> = ({ label, date, done }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <span style={{ width: 14, height: 14, borderRadius: '50%', background: done ? '#4caf50' : '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: '#fff', fontWeight: 700 }}>
      {done ? '✓' : ''}
    </span>
    <span style={{ color: done ? '#333' : '#bbb', fontWeight: done ? 600 : 400 }}>{label}</span>
    {date && <span style={{ color: '#888' }}>({formatDate(date)})</span>}
  </div>
);

export default ReportsLibrary;
